import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { serviceAPI } from '../lib/api';
import { BarChart3, TrendingUp, TrendingDown, Minus, Loader2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function ValuationPage() {
  const { t } = useLanguage();
  const [domain, setDomain] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleValuate = async (e) => {
    e.preventDefault();
    if (!domain.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await serviceAPI.valuation({ domain_name: domain });
      setResult(res.data);
    } catch { setResult(null); }
    setLoading(false);
  };

  const impactIcon = (impact) => {
    if (impact === 'positive') return <TrendingUp className="h-4 w-4 text-[#00FF94]" />;
    if (impact === 'negative') return <TrendingDown className="h-4 w-4 text-[#FF0055]" />;
    return <Minus className="h-4 w-4 text-zinc-500" />;
  };

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="valuation-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <BarChart3 className="h-10 w-10 text-[#00E5FF] mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('valuation.title')}</h1>
          <p className="text-zinc-500 mt-3">{t('valuation.subtitle')}</p>
        </div>

        <form onSubmit={handleValuate} className="flex gap-3 mb-12">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-600" />
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={t('valuation.enterDomain')}
              className="pl-10 h-14 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm text-lg" data-testid="valuation-input" />
          </div>
          <Button type="submit" disabled={loading}
            className="h-14 px-8 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="valuation-submit">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('valuation.valuate')}
          </Button>
        </form>

        {result && (
          <div className="animate-fade-up space-y-6" data-testid="valuation-result">
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 text-center">
              <h3 className="text-2xl font-bold text-white font-['Outfit'] mb-2">{result.domain}</h3>
              <div className="flex items-center justify-center gap-8 mt-6">
                <div>
                  <span className="text-xs text-zinc-600 uppercase tracking-wider block">{t('valuation.score')}</span>
                  <div className="text-5xl font-black text-[#00E5FF] font-['Outfit'] mt-1">{result.score}<span className="text-lg text-zinc-500">/100</span></div>
                </div>
                <div className="h-16 w-px bg-white/10" />
                <div>
                  <span className="text-xs text-zinc-600 uppercase tracking-wider block">{t('valuation.estimatedValue')}</span>
                  <div className="text-3xl font-bold text-white font-['Outfit'] mt-1">${result.estimated_value?.mid?.toLocaleString()}</div>
                  <div className="text-xs text-zinc-500 mt-1">${result.estimated_value?.low?.toLocaleString()} - ${result.estimated_value?.high?.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {result.factors?.length > 0 && (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6">
                <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">{t('valuation.factors')}</h4>
                <div className="space-y-3">
                  {result.factors.map((f, i) => (
                    <div key={i} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0">
                      {impactIcon(f.impact)}
                      <div>
                        <div className="text-sm text-white font-medium">{f.factor}</div>
                        <div className="text-xs text-zinc-500">{f.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
